/**
 * Document Numbering Setup Step
 *
 * Configure document numbering series during company setup
 */

import { Fragment, useState, useEffect, useMemo } from 'react'

import { Checkbox } from '@elsapiens/ui'

import { BulkPrefixEditor, generatePrefixes } from '../document-numbering'
import { Input } from '../ui'

import type { CompanySetupData } from '../../types/company-setup'

interface DocumentNumberingSetupStepProps {
  data: CompanySetupData
  onChange: (updates: Partial<CompanySetupData>) => void
  onNext: () => void
  onBack: () => void
}

// `international` rows (Export / Import) only show when the business trades
// internationally. `finalOnly` rows (URP / Import) have no proforma/draft.
const DEFAULT_SERIES = {
  // Sales (Customer role)
  b2b: { role: 'customer', draft: 'PB', final: 'B', label: 'B2B Invoices', description: 'Sales to registered (GSTIN) customers' },
  b2c: { role: 'customer', draft: 'PC', final: 'C', label: 'B2C Invoices', description: 'Sales to unregistered customers' },
  export: { role: 'customer', draft: 'PE', final: 'E', label: 'Export Invoices', description: 'International sales', international: true },
  creditNote: { role: 'customer', draft: 'PCN', final: 'CN', label: 'Credit Notes', description: 'Sales returns and adjustments' },
  debitNote: { role: 'customer', draft: 'PDN', final: 'DN', label: 'Debit Notes', description: 'Additional charges' },
  // Purchases (Supplier role)
  vendorBill: { role: 'supplier', draft: 'PVB', final: 'VB', label: 'Vendor Bills', description: 'Purchases from registered suppliers' },
  urp: { role: 'supplier', draft: '', final: 'URP', label: 'Purchase URP', description: 'Purchases from unregistered suppliers', finalOnly: true },
  import: { role: 'supplier', draft: '', final: 'IMP', label: 'Import Purchases', description: 'Imports from overseas suppliers', international: true, finalOnly: true },
  vendorCreditNote: { role: 'supplier', draft: 'PVCN', final: 'VCN', label: 'Vendor Credit Notes', description: 'Purchase returns and adjustments' },
} as const

type DocType = keyof typeof DEFAULT_SERIES

function isInternational(docType: DocType): boolean {
  return 'international' in DEFAULT_SERIES[docType]
}

function isFinalOnly(docType: DocType): boolean {
  return 'finalOnly' in DEFAULT_SERIES[docType]
}

const ROLE_GROUPS: { role: 'customer' | 'supplier'; label: string; docTypes: DocType[] }[] = [
  { role: 'customer', label: 'Sales (Customer)', docTypes: ['b2b', 'b2c', 'export', 'creditNote', 'debitNote'] },
  { role: 'supplier', label: 'Purchases (Supplier)', docTypes: ['vendorBill', 'urp', 'import', 'vendorCreditNote'] },
]

const DEFAULT_CUSTOM_PREFIXES: Record<string, { draft: string; final: string }> = {
  b2b: { draft: 'PB', final: 'B' },
  export: { draft: 'PE', final: 'E' },
  b2c: { draft: 'PC', final: 'C' },
  creditNote: { draft: 'PCN', final: 'CN' },
  debitNote: { draft: 'PDN', final: 'DN' },
  vendorBill: { draft: 'PVB', final: 'VB' },
  vendorCreditNote: { draft: 'PVCN', final: 'VCN' },
}

const COLUMN_HEADER_CLASS = 'px-4 py-3 text-left text-xs font-semibold text-foreground-secondary uppercase tracking-wider'

function buildCustomSeries(
  prefixes: Record<string, { draft: string; final: string }>,
): Record<string, { draftPrefix: string; finalPrefix: string }> {
  return {
    b2b: { draftPrefix: prefixes.b2b?.draft ?? '', finalPrefix: prefixes.b2b?.final ?? '' },
    export: { draftPrefix: prefixes.export?.draft ?? '', finalPrefix: prefixes.export?.final ?? '' },
    b2c: { draftPrefix: prefixes.b2c?.draft ?? '', finalPrefix: prefixes.b2c?.final ?? '' },
  }
}

interface NumberingState {
  shouldUseDefaults: boolean
  shouldShowBulkEdit: boolean
  fiscalYear: string
  handleToggleDefaults: (value: boolean) => void
  handlePrefixChange: (docType: string, series: 'draft' | 'final', value: string) => void
  handleBulkApply: (draftPrefix: string, finalPrefix: string) => void
  toggleBulkEdit: () => void
  cancelBulkEdit: () => void
  getPrefixes: (docType: DocType) => { draft: string; final: string }
  generatePreview: (prefix: string) => string
}

function useNumberingInit(
  data: CompanySetupData,
  onChange: (updates: Partial<CompanySetupData>) => void,
): string {
  useEffect(() => {
    if (!data.documentNumbering) {
      onChange({ documentNumbering: { useDefaultSeries: true } })
    }
  }, [data.documentNumbering, onChange])

  return useMemo(() => {
    if (!data.companyInfo.fiscalYearStart) {return ''}
    const startDate = new Date(data.companyInfo.fiscalYearStart)
    const startYear = startDate.getFullYear()
    const endYear = startYear + 1
    return `${startYear.toString().slice(-2)}${endYear.toString().slice(-2)}`
  }, [data.companyInfo.fiscalYearStart])
}

type PrefixMap = Record<string, { draft: string; final: string }>

interface PrefixChangeArgs {
  data: CompanySetupData
  customPrefixes: PrefixMap
  docType: string
  series: 'draft' | 'final'
  value: string
  onChange: (updates: Partial<CompanySetupData>) => void
}

function applyPrefixChange({ data, customPrefixes, docType, series, value, onChange }: PrefixChangeArgs): PrefixMap {
  const newPrefixes = {
    ...customPrefixes,
    [docType]: { ...(customPrefixes[docType] ?? { draft: '', final: '' }), [series]: value.toUpperCase() },
  }
  if (docType === 'b2b' || docType === 'export' || docType === 'b2c') {
    const fieldKey = series === 'draft' ? 'draftPrefix' : 'finalPrefix'
    onChange({
      documentNumbering: {
        ...data.documentNumbering, useDefaultSeries: false,
        customSeries: {
          ...data.documentNumbering?.customSeries,
          [docType]: { ...data.documentNumbering?.customSeries?.[docType], [fieldKey]: value.toUpperCase() },
        },
      },
    })
  }
  return newPrefixes
}

function getDocPrefixes(
  docType: DocType, useDefaults: boolean, customPrefixes: PrefixMap,
): { draft: string; final: string } {
  if (useDefaults) {
    return { draft: DEFAULT_SERIES[docType].draft, final: DEFAULT_SERIES[docType].final }
  }
  return customPrefixes[docType] ?? { draft: DEFAULT_SERIES[docType].draft, final: DEFAULT_SERIES[docType].final }
}

function useDocumentNumbering(
  data: CompanySetupData,
  onChange: (updates: Partial<CompanySetupData>) => void,
): NumberingState {
  const [shouldUseDefaults, setShouldUseDefaults] = useState(data.documentNumbering?.useDefaultSeries ?? true)
  const [customPrefixes, setCustomPrefixes] = useState<PrefixMap>({ ...DEFAULT_CUSTOM_PREFIXES })
  const [shouldShowBulkEdit, setShouldShowBulkEdit] = useState(false)
  const fiscalYear = useNumberingInit(data, onChange)

  const handleToggleDefaults = (value: boolean): void => {
    setShouldUseDefaults(value)
    if (value) { setCustomPrefixes({ ...DEFAULT_CUSTOM_PREFIXES }) }
    onChange({
      documentNumbering: { useDefaultSeries: value, customSeries: value ? undefined : buildCustomSeries(customPrefixes) },
    })
  }

  const handlePrefixChange = (docType: string, series: 'draft' | 'final', value: string): void => {
    setCustomPrefixes(applyPrefixChange({ data, customPrefixes, docType, series, value, onChange }))
    setShouldUseDefaults(false)
  }

  const handleBulkApply = (draftPrefix: string, finalPrefix: string): void => {
    const newPrefixes = generatePrefixes(draftPrefix, finalPrefix)
    setCustomPrefixes(newPrefixes)
    setShouldUseDefaults(false)
    onChange({ documentNumbering: { useDefaultSeries: false, customSeries: buildCustomSeries(newPrefixes) } })
    setShouldShowBulkEdit(false)
  }

  return {
    shouldUseDefaults, shouldShowBulkEdit, fiscalYear, handleToggleDefaults, handlePrefixChange, handleBulkApply,
    toggleBulkEdit: (): void => setShouldShowBulkEdit(!shouldShowBulkEdit),
    cancelBulkEdit: (): void => setShouldShowBulkEdit(false),
    getPrefixes: (docType: DocType): { draft: string; final: string } => getDocPrefixes(docType, shouldUseDefaults, customPrefixes),
    generatePreview: (prefix: string): string => `${prefix}-${fiscalYear}00001`,
  }
}

interface ExplanationSectionProps {
  fiscalYear: string
}

function ExplanationSection({ fiscalYear }: ExplanationSectionProps): JSX.Element {
  return (
    <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        <div>
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Dual Numbering System</h3>
          <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
            Each document type uses separate numbering for draft (proforma) and final posted documents:
          </p>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
            <li><strong>Draft documents</strong> get proforma numbers (e.g., PB-{fiscalYear}00001)</li>
            <li><strong>Posted documents</strong> get final numbers (e.g., B-{fiscalYear}00001)</li>
            <li>Both numbers are stored for audit trail, only final number appears on posted documents</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function SetupOptionsGuide(): JSX.Element {
  return (
    <div className="bg-gradient-to-r from-muted to-purple-50 dark:from-muted dark:to-purple-950 border-2 border-border rounded-lg p-5">
      <h3 className="font-bold text-foreground mb-3 text-lg">Choose Your Setup Method:</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-surface rounded-lg p-4 border-2 border-green-300 dark:border-green-700">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">✅</span>
            <h4 className="font-semibold text-foreground">Option 1: Use Defaults (Recommended)</h4>
          </div>
          <p className="text-sm text-foreground-secondary mb-2">Keep the checkbox checked for standard prefixes:</p>
          <ul className="text-sm text-foreground-secondary space-y-1 ml-4 list-disc">
            <li>B2B: PB → B</li>
            <li>Export: PE → E</li>
            <li>B2C: PC → C</li>
          </ul>
        </div>
        <div className="bg-surface rounded-lg p-4 border-2 border-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⚡</span>
            <h4 className="font-semibold text-foreground">Option 2: Custom Prefixes (Bulk Edit)</h4>
          </div>
          <p className="text-sm text-foreground-secondary mb-2">Uncheck the checkbox, then click &quot;⚡ Set All Prefixes at Once&quot;:</p>
          <ul className="text-sm text-foreground-secondary space-y-1 ml-4 list-disc">
            <li>Set pattern once (e.g., &quot;PRO&quot;)</li>
            <li>Applies to all 10 series instantly</li>
            <li>Example: PROB, PROE, PROC...</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

interface QuickActionsSectionProps {
  shouldUseDefaults: boolean
  shouldShowBulkEdit: boolean
  onToggleDefaults: (value: boolean) => void
  onToggleBulkEdit: () => void
  onBulkApply: (draftPrefix: string, finalPrefix: string) => void
  onCancelBulkEdit: () => void
}

function QuickActionsSection({
  shouldUseDefaults,
  shouldShowBulkEdit,
  onToggleDefaults,
  onToggleBulkEdit,
  onBulkApply,
  onCancelBulkEdit,
}: QuickActionsSectionProps): JSX.Element {
  return (
    <div className="border-2 border-border rounded-lg p-5 bg-surface">
      <div className="flex items-start justify-between gap-4 mb-4">
        <Checkbox
          hideMessage
          checked={shouldUseDefaults}
          description="Standard prefixes: PB/B, PE/E, PC/C, PCN/CN, PDN/DN"
          label="Use Default Numbering Series (Recommended)"
          onChange={(checked: boolean): void => onToggleDefaults(checked)}
        />
        <div className="flex flex-col items-end gap-2">
          <button
            className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:bg-muted-foreground disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors whitespace-nowrap"
            disabled={shouldUseDefaults}
            title={shouldUseDefaults ? 'Uncheck "Use Default Numbering Series" to customize prefixes' : 'Click to set all prefixes at once'}
            type="button"
            onClick={onToggleBulkEdit}
          >
            {shouldShowBulkEdit ? 'Hide Bulk Edit' : '⚡ Set All Prefixes at Once'}
          </button>
          {shouldUseDefaults && (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-right max-w-xs">
              💡 Uncheck the checkbox to customize prefixes
            </p>
          )}
        </div>
      </div>

      {shouldShowBulkEdit && !shouldUseDefaults && (
        <div className="mt-4 -mx-5">
          <BulkPrefixEditor
            applyButtonText="✓ Apply to Sales Series"
            description="Enter a pattern below and it will be applied to the sales document types (B2B, Export, B2C, Credit Notes, Debit Notes). Purchase series use their own defaults."
            title="Bulk Set Prefixes"
            onApply={onBulkApply}
            onCancel={onCancelBulkEdit}
          />
        </div>
      )}
    </div>
  )
}

interface SeriesTableRowProps {
  docType: DocType
  config: { draft: string; final: string; label: string; description: string }
  prefixes: { draft: string; final: string }
  disabled: boolean
  generatePreview: (prefix: string) => string
  onPrefixChange: (docType: string, series: 'draft' | 'final', value: string) => void
}

function SeriesTableRow({
  docType, config, prefixes, disabled, generatePreview, onPrefixChange,
}: SeriesTableRowProps): JSX.Element {
  const hasNoDraft = isFinalOnly(docType)
  return (
    <tr className="hover:bg-surface-secondary">
      <td className="px-4 py-4">
        <div>
          <div className="font-medium text-foreground">{config.label}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{config.description}</div>
        </div>
      </td>
      <td className="px-4 py-4">
        {hasNoDraft ? (
          <span className="text-xs text-foreground-secondary">—</span>
        ) : (
          <Input
            className="w-24" disabled={disabled} maxLength={10} placeholder={config.draft}
            value={prefixes.draft} onChange={(e): void => onPrefixChange(docType, 'draft', e.target.value)}
          />
        )}
      </td>
      <td className="px-4 py-4">
        {hasNoDraft ? (
          <span className="text-xs text-foreground-secondary">—</span>
        ) : (
          <code className="text-xs font-mono text-foreground-secondary bg-surface-secondary px-2 py-1 rounded">
            {generatePreview(prefixes.draft)}
          </code>
        )}
      </td>
      <td className="px-4 py-4">
        <Input
          className="w-24" disabled={disabled} maxLength={10} placeholder={config.final}
          value={prefixes.final} onChange={(e): void => onPrefixChange(docType, 'final', e.target.value)}
        />
      </td>
      <td className="px-4 py-4">
        <code className="text-xs font-mono text-foreground-secondary bg-surface-secondary px-2 py-1 rounded">
          {generatePreview(prefixes.final)}
        </code>
      </td>
    </tr>
  )
}

function SeriesTableHeader(): JSX.Element {
  return (
    <thead className="bg-surface-secondary">
      <tr>
        <th className={COLUMN_HEADER_CLASS}>Document Type</th>
        <th className={COLUMN_HEADER_CLASS}>Draft Prefix</th>
        <th className={COLUMN_HEADER_CLASS}>Draft Preview</th>
        <th className={COLUMN_HEADER_CLASS}>Final Prefix</th>
        <th className={COLUMN_HEADER_CLASS}>Final Preview</th>
      </tr>
    </thead>
  )
}

interface SeriesTableProps {
  shouldUseDefaults: boolean
  showInternational: boolean
  getPrefixes: (docType: DocType) => { draft: string; final: string }
  generatePreview: (prefix: string) => string
  onPrefixChange: (docType: string, series: 'draft' | 'final', value: string) => void
}

function RoleGroupHeaderRow({ label }: { label: string }): JSX.Element {
  return (
    <tr className="bg-surface-secondary/60">
      <td className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-secondary" colSpan={5}>
        {label}
      </td>
    </tr>
  )
}

function SeriesTable({ shouldUseDefaults, showInternational, getPrefixes, generatePreview, onPrefixChange }: SeriesTableProps): JSX.Element {
  return (
    <div className="border-2 border-border rounded-lg overflow-hidden bg-surface">
      <div className="px-5 py-4 bg-surface-secondary border-b border-border">
        <h3 className="font-semibold text-foreground">Document Numbering Series</h3>
        <p className="text-sm text-foreground-secondary mt-1">
          Default series are created for both Customer (sales) and Supplier (purchase) roles.
          Registered/unregistered series are added automatically — fine-tune them later in
          Settings → Document Numbering.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <SeriesTableHeader />
          <tbody className="divide-y divide-border">
            {ROLE_GROUPS.map((group) => {
              const docTypes = group.docTypes.filter(dt => showInternational || !isInternational(dt))
              return (
                <Fragment key={group.role}>
                  <RoleGroupHeaderRow label={group.label} />
                  {docTypes.map((docType) => (
                    <SeriesTableRow
                      key={docType} config={DEFAULT_SERIES[docType]} disabled={shouldUseDefaults}
                      docType={docType} generatePreview={generatePreview}
                      prefixes={getPrefixes(docType)} onPrefixChange={onPrefixChange}
                    />
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function FormatInfoSection(): JSX.Element {
  return (
    <div className="bg-surface-secondary border border-border rounded-lg p-4">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-foreground-secondary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        <div className="text-sm text-foreground-secondary">
          <p className="font-medium text-foreground mb-2">Number Format</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Format: <code className="bg-muted px-1 rounded">{'{PREFIX}-{YYYY}{######}'}</code></li>
            <li><strong>YYYY</strong> = Fiscal year (e.g., 2526 for FY 2025-26)</li>
            <li><strong>######</strong> = 6-digit sequence with leading zeros</li>
            <li>Series reset yearly at fiscal year start</li>
            <li>All settings can be customized later from Settings → Document Numbering</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export function DocumentNumberingSetupStep({
  data, onChange, onNext, onBack,
}: DocumentNumberingSetupStepProps): JSX.Element {
  const state = useDocumentNumbering(data, onChange)
  const [shouldShowInternational, setShouldShowInternational] = useState(true)

  return (
    <div className="space-y-6">
      <ExplanationSection fiscalYear={state.fiscalYear} />
      <SetupOptionsGuide />
      <QuickActionsSection
        shouldShowBulkEdit={state.shouldShowBulkEdit} shouldUseDefaults={state.shouldUseDefaults}
        onBulkApply={state.handleBulkApply} onCancelBulkEdit={state.cancelBulkEdit}
        onToggleBulkEdit={state.toggleBulkEdit} onToggleDefaults={state.handleToggleDefaults}
      />
      <div className="border border-border rounded-lg px-5 py-3 bg-surface">
        <Checkbox
          hideMessage
          checked={shouldShowInternational}
          description="Show Export (sales) and Import (purchase) series. Turn off for domestic-only businesses."
          label="We trade internationally (export / import)"
          onChange={(checked: boolean): void => setShouldShowInternational(checked)}
        />
      </div>
      <SeriesTable
        generatePreview={state.generatePreview} getPrefixes={state.getPrefixes}
        shouldUseDefaults={state.shouldUseDefaults} showInternational={shouldShowInternational}
        onPrefixChange={state.handlePrefixChange}
      />
      <FormatInfoSection />
      <div className="flex justify-between pt-4 border-t border-border">
        <button
          className="px-6 py-2.5 bg-muted text-foreground-secondary rounded-lg hover:bg-muted font-medium transition-colors"
          onClick={onBack}
        >
          Back
        </button>
        <button
          className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors"
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </div>
  )
}
